/**
 * Pure optimistic patches for a meeting's unified `speakers[]` read model.
 *
 * Used by confirm / reject / rename / link mutations so the name the user just
 * picked appears immediately and rolls back if the request fails — no flicker
 * back to "Speaker N" while the meeting query refetches.
 */

import type { ISpeakerEntry, TSpeakerEntryStatus } from "@lexicanon/shared"

export type TSpeakerOptimisticPatch =
  | { kind: "confirm"; label: string; name: string }
  | { kind: "reject-suggestion"; label: string }
  | { kind: "reject-named"; entityId?: string; label: string | null }
  | { kind: "rename"; entityId?: string; label: string | null; name: string }
  | {
      kind: "link"
      entityId?: string
      label: string | null
      name: string
      speakerId: string
      /** Person's saved colour. Null/omitted keeps the slot hue (no flash to a name hash). */
      color?: string | null
    }

function matches(entry: ISpeakerEntry, entityId: string | undefined, label: string | null | undefined): boolean {
  if (entityId !== undefined && entityId.length > 0 && entry.id === entityId) return true
  if (label != null && label.length > 0 && entry.label === label) return true
  return false
}

function named(entry: ISpeakerEntry, name: string, extra: Partial<ISpeakerEntry> = {}): ISpeakerEntry {
  const status: TSpeakerEntryStatus = "confirmed"
  return {
    ...entry,
    displayName: name,
    status,
    suggestion: undefined,
    confirmedBy: "user",
    ...extra,
  }
}

function unnamed(entry: ISpeakerEntry): ISpeakerEntry {
  const fallback = entry.label ?? entry.displayName
  return {
    ...entry,
    displayName: fallback,
    status: "unidentified",
    speakerId: null,
    suggestion: undefined,
    confirmedBy: null,
    voiceSaved: false,
    recognitionSource: null,
  }
}

/**
 * Return a new `speakers[]` with one entry patched. Unknown patches (no matching
 * entity / label) leave the array unchanged in content, still a new array so
 * callers can swap it into React Query without a special-case.
 */
export function applySpeakerOptimistic(
  speakers: readonly ISpeakerEntry[],
  patch: TSpeakerOptimisticPatch,
): ISpeakerEntry[] {
  return speakers.map((entry) => {
    switch (patch.kind) {
      case "confirm":
        if (entry.label !== patch.label) return entry
        return named(entry, patch.name, { voiceSaved: true, recognitionSource: "manual" })
      case "reject-suggestion":
        if (entry.label !== patch.label) return entry
        return unnamed(entry)
      case "reject-named":
        if (!matches(entry, patch.entityId, patch.label)) return entry
        return unnamed(entry)
      case "rename":
        if (!matches(entry, patch.entityId, patch.label)) return entry
        return named(entry, patch.name, { recognitionSource: "manual" })
      case "link":
        if (!matches(entry, patch.entityId, patch.label)) return entry
        return named(entry, patch.name, {
          speakerId: patch.speakerId,
          voiceSaved: true,
          recognitionSource: "manual",
          ...(patch.color != null && patch.color.length > 0 ? { color: patch.color } : {}),
        })
    }
    return entry
  })
}
